import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingCommentCount, Laboratory, Rating } from '@medprev-web-app/api-graphql-client';
import { ReactNode } from 'react';

import { CommentLoading } from '@/app/(main)/(pages)/_components/entities/entity-comment-loading.component';
import {
  EntityLoadMoreComments,
  LoadCommentsPage,
} from '@/app/(main)/(pages)/_components/entities/entity-load-more-comments.component';
import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import { RatingCard } from '@/app/(main)/(pages)/_components/entities/entity-rating-card.component';
import { LABORATORY_PROFILE_COMMENTS_PAGE_SIZE } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-profile-from-search-api.service';
import { RatingComment } from '@/types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { cn } from '@/utils/cn-tw-merge';

interface BranchLaboratoryRatingSectionProps {
  laboratory: Laboratory;
  countComments?: EntityRatingCommentCount | null;
  initialComments?: RatingComment[];
  hasSimilarLaboratories?: boolean;
  loadCommentsPage?: LoadCommentsPage;
  hasMoreComments?: boolean;
  commentsSort?: ReactNode;
  commentsKey?: string;
  isSortingComments?: boolean;
}

function getRating(laboratory: Laboratory): Rating | null {
  const { rating } = laboratory;

  if (!rating) {
    return null;
  }

  return {
    ...rating,
    fiveStars: {
      tiers: {
        one: rating.fiveStars?.tiers.one || 0,
        two: rating.fiveStars?.tiers.two || 0,
        three: rating.fiveStars?.tiers.three || 0,
        four: rating.fiveStars?.tiers.four || 0,
        five: rating.fiveStars?.tiers.five || 0,
      },
      average: rating.fiveStars?.average || 0,
      count: rating.fiveStars?.count || 0,
    },
  };
}

export function BranchLaboratoryRatingSection({
  laboratory,
  countComments,
  initialComments,
  hasSimilarLaboratories,
  loadCommentsPage,
  hasMoreComments,
  commentsSort,
  commentsKey,
  isSortingComments,
}: BranchLaboratoryRatingSectionProps) {
  const rating = getRating(laboratory);
  const hasComments = Number(countComments?.total) > 0;

  return (
    <>
      <EntitySection
        data-testid={DataTestIdEnum.LABORATORY_PROFILE_SECTION_RATING}
        className={cn(commentsSort && 'my-0')}
        removeHr={Boolean(commentsSort) || (Number(countComments?.total) === 0 && !hasSimilarLaboratories)}
      >
        <RatingCard
          rating={rating as Rating}
          countComments={countComments}
          commentsSort={commentsSort}
        />
      </EntitySection>

      {hasComments && isSortingComments && (
        <div aria-busy data-testid={DataTestIdEnum.ENTITY_COMMENTS_LOADING}>
          {Array.from({ length: LABORATORY_PROFILE_COMMENTS_PAGE_SIZE }).map((_, index) => (
            <CommentLoading key={index} />
          ))}
        </div>
      )}

      {hasComments && !isSortingComments && (
        <EntityLoadMoreComments
          key={commentsKey}
          countComments={countComments}
          partnerFacilityId={laboratory.id as string}
          initialComments={initialComments}
          loadPage={loadCommentsPage}
          hasMore={hasMoreComments}
          className={cn(commentsSort && 'mb-0 md:mb-0')}
          commentClassName={cn(commentsSort && 'first:mt-3')}
          analyticsEventShowMore={AnalyticsEnum.LABORATORY_PROFILE_EXPAND_COMMENT}
          analyticsEventShowMoreComments={AnalyticsEnum.LABORATORY_PROFILE_CLICK_SHOW_MORE_COMMENTS}
        />
      )}
    </>
  );
}
